import {
    centsToFormattedDollars,
    formattedDollarsToCents,
} from '@fruitful/fruitils';
import {
    stripMoneyMapAccountFromRules,
    updateRuleAndGroupPriorities,
} from '@fruitful/money-map';
import {
    AccountOwnership,
    type DeepPartial,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountWithAccountDetails,
    MoneyMapCategory,
    MoneyMapRuleType,
    type MoneyMapRuleUpsertDto,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
    NEW_CASH_ACCOUNT_OPTION,
    PaycheckRoutingRuleType,
} from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { capitalize } from 'lodash';
import { useEffect, useMemo } from 'react';
import {
    type SubmitHandler,
    useForm,
    type UseFormReturn,
} from 'react-hook-form';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    Form,
    H6Emphasized,
    USE_FORM_MODE_DEFAULTS,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { selectedMembershipAtom } from '../../../entities/entityAtoms';
import { useMoneyMapAccounts } from '../../providers/MoneyMapAccountsProvider';
import {
    validateAccountNameLength,
    validateIndividualAccountOwnership,
    validatePhysicalCardLimits,
    validateRequiredFields,
    validateTransferAccountSettingsExist,
} from '../../utils/moneyMapAccountFormUtils';
import { buildMoneyMapAccountForMoneyMapRuleUpsertDto } from '../../utils/moneyMapRuleUpsertDtoBuilder';
import {
    getDefaultAccountSettings,
    shouldUseTransferAccount,
} from '../../utils/moneyMapUtils';
import {
    AccountSettingsFormModule,
    accountSettingsFormSchema,
    type GenericAccountSettingsFormSchema,
    transferAccountSettingsFormSchema,
} from './AccountSettingsFormModule';
import { AmountFormModule } from './AmountFormModule';
import FormDrawer from './FormDrawer';

interface BillsSpendFormProps {
    rule?: DeepPartial<MoneyMapRuleWithAccountDetails>;
    ruleType: MoneyMapRuleType.BILLS | MoneyMapRuleType.SPEND;
    priority: number;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    onPressSave: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<void>;
    onPressRemove: (ruleId?: string) => Promise<void>;
    onClose?: () => void;
    presentation?: 'drawer' | 'modal';
}

const BillsSpendForm = ({
    rule,
    ruleType,
    priority,
    moneyMap,
    onPressSave,
    onPressRemove,
    onClose,
    presentation,
}: BillsSpendFormProps) => {
    const { accounts } = useMoneyMapAccounts();
    const membership = useAtomValue(selectedMembershipAtom);
    const isSoloMembership = membership?.users?.length === 1;

    const {
        targetBalanceCents,
        monthlyContributionMaxCents,
        moneyMapAccount: account,
    } = rule ?? {};

    const incomeAccount:
        | DeepPartial<MoneyMapAccountWithAccountDetails>
        | undefined = useMemo(() => {
        if (moneyMap?.incomeMoneyMapAccountId != null) {
            return accounts.find(
                (account) => account.id === moneyMap.incomeMoneyMapAccountId,
            );
        }

        return moneyMap.incomeMoneyMapAccount;
    }, [JSON.stringify(moneyMap)]);

    const existingAccountForTransfer = useMemo(() => {
        if (account?.moneyMapAccountIdForTransfer != null) {
            return accounts.find(
                (a) => a.id === account.moneyMapAccountIdForTransfer,
            );
        }

        return account?.moneyMapAccountForTransfer;
    }, [JSON.stringify(accounts), account]);

    const category =
        ruleType === MoneyMapRuleType.BILLS
            ? MoneyMapCategory.BILLS
            : MoneyMapCategory.SPEND;

    const billsSpendFormSchema = z
        .object({
            monthlyAmount: z
                .string()
                .min(3, { message: 'Monthly amount is required' }), // Account for "$ " prepending formatted dollar amount
            ...accountSettingsFormSchema.shape,
            ...transferAccountSettingsFormSchema.shape,
        })
        .superRefine(({ accountSettings, transferAccountSettings }, ctx) => {
            const isNewCashAccount =
                accountSettings.moneyMapAccountId ===
                NEW_CASH_ACCOUNT_OPTION.value;
            const isNewCashAccountForTransfer =
                transferAccountSettings?.moneyMapAccountId ===
                NEW_CASH_ACCOUNT_OPTION.value;

            const account = accounts.find(
                (account) => account.id === accountSettings.moneyMapAccountId,
            );
            const transferAccount = accounts.find(
                (account) =>
                    account.id === transferAccountSettings?.moneyMapAccountId,
            );

            const isProposedCashAccount =
                account?.status === MoneyMapAccountStatus.PROPOSED &&
                account?.type === MoneyMapAccountType.INTERNAL_BANKING;
            const isProposedTransferCashAccount =
                transferAccount?.status === MoneyMapAccountStatus.PROPOSED &&
                transferAccount?.type === MoneyMapAccountType.INTERNAL_BANKING;

            const isTransferAccountRequired = shouldUseTransferAccount(
                account,
                accountSettings.moneyMapAccountId,
                incomeAccount,
            );
            const isTransferAccountValid = isTransferAccountRequired
                ? validateTransferAccountSettingsExist(
                      transferAccountSettings,
                      ctx,
                  )
                : true;

            const isAccountPhysicalCardValid = validatePhysicalCardLimits(
                'account',
                accounts,
                isSoloMembership,
                accountSettings,
                account,
                ctx,
            );

            const isTransferAccountPhysicalCardValid =
                validatePhysicalCardLimits(
                    'transferAccount',
                    accounts,
                    isSoloMembership,
                    transferAccountSettings,
                    transferAccount,
                    ctx,
                );

            const isAccountOwnershipValid =
                accountSettings.ownership === AccountOwnership.INDIVIDUAL
                    ? validateIndividualAccountOwnership(
                          'account',
                          accountSettings.primaryUserId,
                          ctx,
                      )
                    : true;

            const isTransferAccountOwnershipValid =
                transferAccountSettings?.ownership ===
                AccountOwnership.INDIVIDUAL
                    ? validateIndividualAccountOwnership(
                          'transferAccount',
                          transferAccountSettings?.primaryUserId,
                          ctx,
                      )
                    : true;

            const isNewOrProposedCashAccountValid =
                isNewCashAccount || isProposedCashAccount
                    ? validateRequiredFields(
                          'account',
                          [
                              ['name', accountSettings.name],
                              ['ownership', accountSettings.ownership],
                              ['category', accountSettings.category],
                              ['virtualCard', accountSettings.virtualCard],
                              ['physicalCard', accountSettings.physicalCard],
                          ],
                          ctx,
                      ) &&
                      validateAccountNameLength(
                          'account',
                          ctx,
                          accountSettings.name,
                      )
                    : true;

            const isNewOrProposedCashAccountForTransferValid =
                isNewCashAccountForTransfer || isProposedTransferCashAccount
                    ? validateRequiredFields(
                          'transferAccount',
                          [
                              ['name', transferAccountSettings?.name],
                              ['ownership', transferAccountSettings?.ownership],
                              ['category', transferAccountSettings?.category],
                              [
                                  'virtualCard',
                                  transferAccountSettings?.virtualCard,
                              ],
                              [
                                  'physicalCard',
                                  transferAccountSettings?.physicalCard,
                              ],
                          ],
                          ctx,
                      ) &&
                      validateAccountNameLength(
                          'transferAccount',
                          ctx,
                          transferAccountSettings?.name,
                      )
                    : true;

            return (
                isTransferAccountValid &&
                isAccountPhysicalCardValid &&
                isTransferAccountPhysicalCardValid &&
                isAccountOwnershipValid &&
                isTransferAccountOwnershipValid &&
                isNewOrProposedCashAccountValid &&
                isNewOrProposedCashAccountForTransferValid
            );
        });

    type BillsSpendFormSchema = z.infer<typeof billsSpendFormSchema>;

    const formFunctions = useForm<BillsSpendFormSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(billsSpendFormSchema),
        defaultValues: {
            monthlyAmount:
                // BILLS persist the monthly figure as targetBalanceCents
                // (MINIMUM_BALANCE); SPEND as monthlyContributionMaxCents
                // (PERCENTAGE). Same product concept, different columns —
                // see PaycheckRoutingRuleType.
                targetBalanceCents != null
                    ? centsToFormattedDollars(targetBalanceCents, false)
                    : monthlyContributionMaxCents != null
                      ? centsToFormattedDollars(
                            monthlyContributionMaxCents,
                            false,
                        )
                      : '',
            accountSettings: getDefaultAccountSettings(
                account ??
                    (ruleType === MoneyMapRuleType.BILLS
                        ? incomeAccount
                        : undefined),
            ),
            transferAccountSettings: getDefaultAccountSettings(
                existingAccountForTransfer,
                true,
            ),
        },
    });

    const selectedMoneyMapAccountId = formFunctions.watch(
        'accountSettings.moneyMapAccountId',
    );

    const selectedMoneyMapAccount = useMemo(() => {
        return accounts.find(
            (account) => account.id === selectedMoneyMapAccountId,
        );
    }, [JSON.stringify(accounts), selectedMoneyMapAccountId]);

    const selectedMoneyMapAccountIdForTransfer = formFunctions.watch(
        'transferAccountSettings.moneyMapAccountId',
    );

    const selectedMoneyMapAccountForTransfer = useMemo(() => {
        return accounts.find(
            (account) => account.id === selectedMoneyMapAccountIdForTransfer,
        );
    }, [JSON.stringify(accounts), selectedMoneyMapAccountIdForTransfer]);

    const onSubmit: SubmitHandler<BillsSpendFormSchema> = async ({
        monthlyAmount,
        accountSettings,
        transferAccountSettings,
    }) => {
        if (accountSettings.moneyMapAccountId == null) return;

        const proposedAccountToUpdate = accounts.find(
            (account) =>
                account.id === accountSettings.moneyMapAccountId &&
                account.status === MoneyMapAccountStatus.PROPOSED,
        );
        const proposedTransferAccountToUpdate = accounts.find(
            (account) =>
                account.id === transferAccountSettings?.moneyMapAccountId &&
                account.status === MoneyMapAccountStatus.PROPOSED,
        );

        const updatedRule: Partial<MoneyMapRuleUpsertDto> = {
            id: rule?.id,
            moneyMapId: moneyMap?.id,
            type: ruleType,
            description: `${capitalize(ruleType)} account`,
            priority,
        };

        if (ruleType === MoneyMapRuleType.BILLS) {
            // Historical shape: monthly bills amount is stored as a
            // MINIMUM_BALANCE target, not monthlyContributionMaxCents.
            // Income split still executes this as a monthly cap.
            // See PaycheckRoutingRuleType.
            updatedRule.routingRuleType =
                PaycheckRoutingRuleType.MINIMUM_BALANCE;
            updatedRule.targetBalanceCents =
                formattedDollarsToCents(monthlyAmount);
            // Explicitly null out PERCENTAGE-only fields -- some legacy rules have
            // both shapes populated (one stale), and omitting a field from the
            // upsert payload leaves its existing persisted value untouched.
            updatedRule.monthlyContributionMaxCents = null;
            updatedRule.yearlyContributionMaxCents = null;
        } else {
            // Default spend rules to maximize contributions up to a monthly limit
            updatedRule.routingRuleType = PaycheckRoutingRuleType.PERCENTAGE;
            updatedRule.percentage = 1.0;
            updatedRule.monthlyContributionMaxCents =
                formattedDollarsToCents(monthlyAmount);
            // Explicitly null out the MINIMUM_BALANCE-only field -- see comment above.
            updatedRule.targetBalanceCents = null;
        }

        const updatedRuleWithAccount =
            buildMoneyMapAccountForMoneyMapRuleUpsertDto(
                updatedRule as MoneyMapRuleUpsertDto,
                { accountSettings },
                { transferAccountSettings },
                category,
                membership,
                selectedMoneyMapAccount,
                proposedAccountToUpdate,
                proposedTransferAccountToUpdate,
                incomeAccount,
            );

        // Exclude existing moneyMapAccount dto object from upsert call.
        const existingRules = stripMoneyMapAccountFromRules(
            moneyMap.moneyMapRules ?? [],
        );

        // Update priorities across all rules and groups
        const { rules: updatedRules, groups: updatedGroups } =
            updateRuleAndGroupPriorities(
                updatedRuleWithAccount,
                existingRules,
                moneyMap.moneyMapRuleGroups ?? [],
                'upsert',
            );

        const incomeAccountId =
            moneyMap.incomeMoneyMapAccountId ??
            moneyMap.incomeMoneyMapAccount?.id;

        const savePayload: DeepPartial<MoneyMapWithRulesAndAccounts> = {
            moneyMapRules: updatedRules,
            moneyMapRuleGroups: updatedGroups,
        };

        if (
            incomeAccountId != null &&
            accountSettings.moneyMapAccountId === incomeAccountId &&
            updatedRuleWithAccount.moneyMapAccount != null
        ) {
            savePayload.incomeMoneyMapAccount = {
                ...moneyMap.incomeMoneyMapAccount,
                ...updatedRuleWithAccount.moneyMapAccount,
                id: incomeAccountId,
            };
        }

        await onPressSave(savePayload);
    };

    const { handleSubmit } = formFunctions;
    const { isValid } = formFunctions.formState;

    const shouldShowTransferAccount = shouldUseTransferAccount(
        selectedMoneyMapAccount,
        selectedMoneyMapAccountId,
        incomeAccount,
    );

    // Reset transferAccountSettings when shouldShowTransferAccount changes
    useEffect(() => {
        if (!shouldShowTransferAccount) {
            formFunctions.setValue('transferAccountSettings', undefined); // Reset transferAccountSettings field
        }
    }, [shouldShowTransferAccount, formFunctions]);

    // Trigger form validation.
    useEffect(() => {
        const subscription = formFunctions.watch(() => {
            void formFunctions.trigger(); // Re-run validation including superRefine
        });

        // Initial trigger for selected account IDs
        void formFunctions.trigger();

        return () => {
            subscription.unsubscribe();
        };
    }, [
        formFunctions,
        selectedMoneyMapAccountId,
        selectedMoneyMapAccountIdForTransfer,
    ]);

    return (
        <FormDrawer
            onClose={onClose}
            presentation={presentation}
            onSave={handleSubmit(onSubmit)}
            saveDisabled={!isValid}
        >
            <Form onSubmit={handleSubmit(onSubmit)}>
                <YStack gap={16}>
                    <XStack alignItems="center" justifyContent="space-between">
                        <View flexGrow={1}>
                            <H6Emphasized>
                                Add {capitalize(ruleType)} account
                            </H6Emphasized>
                        </View>
                        <Button
                            variant={ButtonVariant.TERTIARY}
                            size={ButtonSize.TINY}
                            width="auto"
                            tone="danger"
                            text="Delete"
                            onPress={() => {
                                void onPressRemove(rule?.id);
                            }}
                        />
                    </XStack>
                    <AmountFormModule
                        name="monthlyAmount"
                        control={formFunctions.control}
                        register={formFunctions.register}
                        errors={formFunctions.formState.errors}
                    />
                    <AccountSettingsFormModule
                        type="account"
                        accounts={accounts}
                        selectedAccount={selectedMoneyMapAccount}
                        formFunctions={
                            formFunctions as never as UseFormReturn<GenericAccountSettingsFormSchema>
                        }
                        ruleType={ruleType}
                    />
                    {shouldShowTransferAccount && (
                        <AccountSettingsFormModule
                            type="transferAccount"
                            accounts={accounts}
                            selectedAccount={selectedMoneyMapAccountForTransfer}
                            formFunctions={
                                formFunctions as never as UseFormReturn<GenericAccountSettingsFormSchema>
                            }
                            ruleType={ruleType}
                        />
                    )}
                </YStack>
            </Form>
        </FormDrawer>
    );
};

export default BillsSpendForm;
